/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Checkbox,
  Heading,
  Icons,
  Loading,
  TextInput,
} from '@sede-enterprise/shell-ds-react-framework';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Table from '../Table';
import Button from '../../../../UI/Button';
import Card from '../../../../UI/Card';
import { Col, FormRow, Row } from '../../../../UI/Grid';
import { sendNotification } from '../../../../UI/Notification';
import {
  DOWNLOAD_TERMINAL_MAPPINGS,
  GET_TERMINAL_MAPPINGS,
  GET_UK_NOMINATION_LOGS,
  MESSAGES,
  UPDATE_NOMINATION_VISIBILITY,
} from '../../../../constants/FieldNominations';
import { get, put } from '../../../../services/GenesisApi';
import { FieldNomsActions } from '../../../../store/reducers/Nominations/FieldNoms';
import { IFullState } from '../../../../types/All.type';
import { IFieldType, IFormula, ITerminalMapping } from '../../../../types/FieldNominations.type';
import {
  checkExisting,
  handleDownload,
  isFieldActive,
  getCurrentGasDay,
} from '../../../../utils/common/utils';
import searchByText from '../../../../utils/searchFunction';
import AddEditTerminalMapping from '../AddEditTerminalMapping';
import FieldAuditLogs from '../FieldAuditLogs';
import UnMappedMapping from '../UnmappedTerminals';
import { TerminalMappingWrapper } from '../style';
import ConfirmDeleteMapping from './ConfirmDeleteMapping';
import { rolePermissions } from '../../../../constants/Permission';

const { DownloadSolid, ArrowLeft, Edit, TrashAlt: DeleteIcon, InfoCircle } = Icons;

const TerminalMapping: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [open, setOpen] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openUnMapped, setOpenUnMapped] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [data, setData] = useState<ITerminalMapping[]>([]);
  const [openAudit, setOpenAudit] = useState(false);

  const [isValidContracts, setIsValidContracts] = useState<boolean>(true);
  const [selectedItem, setSelectedItem] = useState<ITerminalMapping | null>(null);
  const [loadingIsVisible, setLoadingIsVisible] = useState<{
    [key: string]: boolean;
  }>({});

  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const getTerminalMappings = (isValid: boolean) => {
    setLoading(true);
    get(GET_TERMINAL_MAPPINGS(isValid))
      .then((response) => {
        setData(response?.data ?? []);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getTerminalMappings(isValidContracts);
  }, [isValidContracts]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleUpdateProdFields = (
    prodFieldId: number,
    sourceProdFieldId: number,
    status: boolean,
  ) => {
    dispatch(
      FieldNomsActions.updateProdFields({
        prodFieldId,
        sourceProdFieldId,
        status,
      }),
    );
  };

  const handleOnOpenClose = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleOnOpenCloseConfirm = (value: boolean) => {
    setOpenConfirm(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleOnOpenCloseAudit = (value: boolean) => {
    setOpenAudit(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleDownloadOnClick = async () => {
    setDownloading(true);
    await handleDownload(
      `${DOWNLOAD_TERMINAL_MAPPINGS}?isValid=${isValidContracts}`,
      `Mappings_${dayjs().format()}.xlsx`,
    );
    setDownloading(false);
  };

  const handleEditOnClick = (item: ITerminalMapping) => {
    setSelectedItem(item);
    handleOnOpenClose(true);
  };

  const handleDeleteOnClick = (item: ITerminalMapping) => {
    setSelectedItem(item);
    handleOnOpenCloseConfirm(true);
  };

  const handleInfoOnClick = (item: ITerminalMapping) => {
    setSelectedItem(item);
    handleOnOpenCloseAudit(true);
  };

  const renderColumnClassName = (willExpire: number) => {
    if (willExpire === 1) {
      return 'row-warning';
    }
    if (willExpire === 2) {
      return 'row-danger';
    }
    return '';
  };

  const renderData = (text: string, mappings: ITerminalMapping[]) => {
    return searchByText(mappings, text, [
      'counterparty',
      'sourceProductionField',
      'productionField',
      'terminal',
      'subTerminal',
      'deal_DealLegId',
      'dealUnit',
      'timer',
      'formula',
      'daVarianceBreach',
      'wdVarianceBreach',
      'swingContract',
    ]).map((row) => ({
      ...row,
      className: renderColumnClassName(row?.willExpire),
    }));
  };

  const handleSetData = (sourceProdFieldId: number, checked: boolean) => {
    setData((prevState) => {
      return prevState.map((item) => {
        if (item.sourceProductionFieldId === sourceProdFieldId) {
          return {
            ...item,
            isVisible: checked,
          };
        }
        return item;
      });
    });
  };

  const handleIsVisible = ({
    productionFieldId,
    sourceProductionFieldId,
    isVisible: checked,
    id,
  }: {
    productionFieldId: number;
    sourceProductionFieldId: number;
    isVisible: boolean;
    id: number;
  }) => {
    setLoadingIsVisible((prevState) => ({
      ...prevState,
      [sourceProductionFieldId]: true,
    }));
    put(
      `${UPDATE_NOMINATION_VISIBILITY}?sourceProductionFieldId=${sourceProductionFieldId}&isVisible=${checked}&fieldMappingId=${id}`,
      {},
    )
      .then((response) => {
        sendNotification({
          type: 'success',
          message: response?.data,
        });
        handleUpdateProdFields(productionFieldId, sourceProductionFieldId, checked);
        setLoadingIsVisible((prevState) => ({
          ...prevState,
          [sourceProductionFieldId]: false,
        }));
        handleSetData(sourceProductionFieldId, checked);
      })
      .catch(() => {
        setLoadingIsVisible((prevState) => ({
          ...prevState,
          [sourceProductionFieldId]: false,
        }));
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      });
  };

  const checkTerminalMappingExists = (frmData) => {
    let objData = data;
    if (selectedItem) {
      objData = data.filter((item) => item.id !== selectedItem.id);
    }
    const mappingExists: boolean = checkExisting(
      [
        'deal_DealLegId',
        'sourceProductionFieldId',
        'productionFieldId',
        'terminalId',
        'subTerminalId',
      ],
      objData,
      frmData,
    );
    if (mappingExists) {
      return {
        error: true,
        message: 'A Production Field and terminal can be mapped to only one Deal ID',
      };
    }
    return {
      error: false,
      message: '',
    };
  };

  const sourceProductionFieldExists = (sourceProductionFieldId: number) => {
    const result = data.filter((_) => _.sourceProductionFieldId === sourceProductionFieldId);
    if (selectedItem) {
      return result.length > 1;
    }
    return result.length > 0;
  };

  const renderActionButtons = (item) => {
    return (
      <div className='grid-actions'>
        <Button
          iconOnly
          icon={<Edit />}
          variant='transparent'
          size='xsmall'
          title='Edit'
          onClick={() => handleEditOnClick(item)}
          disabled={!permissions.includes(rolePermissions.updateUkNomination)}
          data-testid={`btn-edit-mapping-${item?.id}`}
        />
        <Button
          iconOnly
          icon={<DeleteIcon />}
          variant='transparent'
          size='xsmall'
          title='Delete'
          onClick={() => handleDeleteOnClick(item)}
          disabled={!permissions.includes(rolePermissions.deleteUkNomination)}
          data-testid={`btn-delete-mapping-${item?.id}`}
        />
        <Button
          iconOnly
          icon={<InfoCircle />}
          variant='transparent'
          size='xsmall'
          title='Audit Logs'
          onClick={() => handleInfoOnClick(item)}
          data-testid={`btn-audit-mapping-${item?.id}`}
        />
      </div>
    );
  };

  const renderImage = (item) => {
    const isActive = isFieldActive(item?.validFrom, item?.validTo);
    return (
      <img
        width={20}
        height={20}
        src={isActive ? '/check-green.png' : '/cross-red.png'}
        alt={isActive ? 'Active' : 'Inactive'}
        title={isActive ? 'Active' : 'Inactive'}
      />
    );
  };

  const renderIsVisible = (item) => {
    return loadingIsVisible[item?.sourceProductionFieldId] ? (
      <Loading />
    ) : (
      <Checkbox
        disabled={!permissions.includes(rolePermissions.updateUkNomination)}
        checked={item?.isVisible}
        onChange={() =>
          handleIsVisible({
            productionFieldId: item?.productionFieldId,
            sourceProductionFieldId: item?.sourceProductionFieldId,
            isVisible: !item?.isVisible,
            id: item?.id,
          })
        }
        data-testid={`chk-isVisible-${item?.id}`}
      />
    );
  };

  const renderFormula = (formula: IFormula[]) => {
    // based on currentGasDay show the formula which is valid for current gas day
    const currentGasDay = getCurrentGasDay();
    const validFormula = formula?.find(
      (f) =>
        dayjs(currentGasDay).isAfter(dayjs(f.validFrom)) &&
        dayjs(currentGasDay).isBefore(dayjs(f.validTo)),
    );
    return validFormula ? validFormula.formula : '';
  };

  const columns = [
    {
      header: '',
      accessorKey: '',
      width: 20,
      cell: ({ row }) => renderImage(row?.original),
    },
    {
      accessorKey: 'deal_DealLegId',
      header: 'Deal ID',
      sorting: true,
    },
    {
      accessorKey: 'dealUnit',
      header: 'Deal Unit',
      sorting: true,
    },
    {
      accessorKey: 'counterparty',
      header: 'Counterparty',
      sorting: true,
    },
    {
      accessorKey: 'sourceProductionField',
      header: 'Source Production Field',
      sorting: true,
    },
    {
      accessorKey: 'productionField',
      header: 'Production Field',
      sorting: true,
    },
    {
      accessorKey: 'terminal',
      header: 'Terminal',
      sorting: true,
    },
    {
      accessorKey: 'subTerminal',
      header: 'Sub Terminal',
      sorting: true,
    },
    {
      accessorKey: 'swingContract',
      header: 'Swing Contract',
      sorting: true,
    },
    {
      accessorKey: 'timer',
      header: 'Deadline Time',
    },
    {
      accessorKey: 'formula',
      header: 'Formula',
      cell: ({ row }) => renderFormula(row?.original?.formula),
    },
    {
      accessorKey: 'Source',
      header: 'Source',
      width: 100,
      cell: ({ row }) =>
        row?.original?.dataSource?.length > 0
          ? row?.original?.dataSource?.filter((s) => s !== null).join(', ')
          : 'N/A',
    },
    {
      accessorKey: 'wdVarianceBreach',
      header: 'WD Variance Breach',
      sorting: true,
    },
    {
      accessorKey: 'daVarianceBreach',
      header: 'DA Variance Breach',
      sorting: true,
    },
    {
      accessorKey: '',
      header: 'Is Visible',
      cell: ({ row }) => renderIsVisible(row?.original),
    },
    {
      accessorKey: '',
      header: 'Valid From',
      cell: ({ row }) => dayjs(row?.original?.validFrom).format('DD MMM YYYY'),
    },
    {
      accessorKey: '',
      header: 'Valid To',
      cell: ({ row }) => dayjs(row?.original?.validTo).format('DD MMM YYYY'),
    },
    {
      accessorKey: '',
      header: 'Actions',
      cell: ({ row }) => renderActionButtons(row?.original),
    },
  ];

  const filteredData = renderData(searchText, data);

  return (
    <TerminalMappingWrapper>
      <Card>
        <Row>
          <Col size={12}>
            <FormRow>
              <Col size={12} className='header-with-close'>
                <Button
                  className='close-mapping'
                  iconOnly
                  icon={<ArrowLeft />}
                  onClick={() => navigate(`/field-noms/field-nominations${location.search}`)}
                  variant='transparent'
                  data-testid='btn-back'
                />
                <Heading type='h2'>Production Field - Terminal - SubTerminal mapping</Heading>
              </Col>
            </FormRow>
            <FormRow className='filter-container'>
              <Col size={6} className='date-range'>
                <TextInput size='small' placeholder='Search' onChange={handleSearch} />
                <Checkbox
                  size='large'
                  label='Valid Mappings'
                  name='Show Valid Mapping'
                  checked={isValidContracts}
                  onChange={() => setIsValidContracts(!isValidContracts)}
                  data-testid='chk-valid-mappings'
                />
              </Col>
              <Col size={6} className='button-wrapper'>
                <Button
                  size='small'
                  onClick={() => handleOnOpenClose(true)}
                  disabled={!permissions.includes(rolePermissions.createUkNomination) || loading}
                  data-testid='btn-add-new-terminal-mapping'
                >
                  Add New
                </Button>
                <Button
                  onClick={() => setOpenUnMapped(true)}
                  disabled={loading}
                  data-testid='btn-unmapped-terminal-mapping'
                >
                  UnMapped
                </Button>
                <Button
                  size='small'
                  icon={<DownloadSolid />}
                  onClick={handleDownloadOnClick}
                  loading={downloading}
                  disabled={
                    !permissions.includes(rolePermissions.downloadUkNomination) ||
                    downloading ||
                    loading
                  }
                  data-testid='btn-download-terminal-mapping'
                >
                  Download
                </Button>
              </Col>
            </FormRow>
            <FormRow>
              <Table
                className='field-terminal-mapping-table'
                columns={columns}
                data={filteredData}
                loading={loading}
                totalRecords={filteredData.length}
                onChangeSortingPaginationFilter={() => {}}
                size='medium'
              />
            </FormRow>
          </Col>
        </Row>
      </Card>
      {open && (
        <AddEditTerminalMapping
          open={open}
          selectedItem={selectedItem}
          onClose={() => handleOnOpenClose(false)}
          refreshData={() => getTerminalMappings(isValidContracts)}
          checkExisting={checkTerminalMappingExists}
          sourceProductionFieldExists={sourceProductionFieldExists}
        />
      )}
      <ConfirmDeleteMapping
        open={openConfirm}
        selectedItem={selectedItem}
        onClose={() => handleOnOpenCloseConfirm(false)}
        refreshData={() => getTerminalMappings(isValidContracts)}
      />
      <UnMappedMapping open={openUnMapped} onClose={() => setOpenUnMapped(false)} />

      {openAudit && (
        <FieldAuditLogs
          open={openAudit}
          url={`${GET_UK_NOMINATION_LOGS}?mappingId=${selectedItem?.id}`}
          onClose={() => handleOnOpenCloseAudit(false)}
          modalTitle='Field Mapping Nomination Info and Audit Logs'
          fieldType={IFieldType.FIELD_MAPPING}
        >
          <>
            <div>
              <span className='title'>Source production Field :</span>{' '}
              {selectedItem?.sourceProductionField}
            </div>
            <div>
              <span className='title'>Production Field :</span> {selectedItem?.productionField}
            </div>
            <div>
              <span className='title'>Sub Terminal :</span> {selectedItem?.subTerminal}
            </div>
            <div>
              <span className='title'>Audit Logs:</span>
            </div>
          </>
        </FieldAuditLogs>
      )}
    </TerminalMappingWrapper>
  );
};

export default TerminalMapping;
